import type {
  TalentFormData,
  TalentFormErrors,
} from "../types/register-talent.types";
import {
  SKIN_TONE_OPTIONS,
  HAIR_TYPE_OPTIONS,
  EXPERIENCE_LEVELS,
} from "../../../shared/constants/talentOptions";
import {
  LANGUAGE_OPTIONS,
  OTHER_LANGUAGE_VALUE,
  getDialectOptions,
  mergeLanguagesAndDialects,
} from "../../../shared/constants/languages";
import {
  isLikelyEmail,
  isValidPhone,
  isValidPassword,
  PASSWORD_REQUIREMENTS_KEY,
  calculateAge,
} from "./validators";

// Every message returned below is a translation KEY, not display text — this
// module is a plain function module (no React/hook context), so it can't
// call t() itself. useRegisterTalent.ts translates each key at the point it
// stores an error into state (it has t() via useLanguage()).

// Re-exported so existing importers (e.g. useRegisterTalent.ts) don't need
// to change — calculateAge now lives in ./validators, the single shared copy.
export { calculateAge };

// ── Constants ────────────────────────────────────────────────────────────────

export { LANGUAGE_OPTIONS, LANGUAGE_OPTIONS as LANGUAGES };
export { SKIN_TONE_OPTIONS, HAIR_TYPE_OPTIONS, EXPERIENCE_LEVELS };

/** Arabic dialect options (derived from the shared languages source) */
export const ARABIC_TYPES = getDialectOptions("arabic").map((d) => d.label);

// ── Talent Categories (NO languages inside) ────────────────────────────────
export const TALENT_CATEGORIES: {
  category: string;
  options: (string | { value: string; label: string })[];
}[] = [
  { category: "Gender", options: ["Male", "Female"] },
];

// ── Per-field validators (return error string or undefined) ──────────────────

const URL_RE = /^https?:\/\/.+\..+/;

export const fieldValidators: Partial<
  Record<
    keyof TalentFormData,
    (v: any, form?: TalentFormData) => string | undefined
  >
> = {
  first_name: (v) => {
    if (!v?.trim()) return "auth.validation.firstNameRequired";
    if (v.trim().length < 2) return "auth.validation.min2Chars";
    if (v.trim().length > 50) return "auth.validation.max50Chars";
    if (!/^[\p{L}\s'-]+$/u.test(v.trim())) return "auth.validation.lettersOnly";
  },
  last_name: (v) => {
    if (!v?.trim()) return "auth.validation.lastNameRequired";
    if (v.trim().length < 2) return "auth.validation.min2Chars";
    if (v.trim().length > 50) return "auth.validation.max50Chars";
    if (!/^[\p{L}\s'-]+$/u.test(v.trim())) return "auth.validation.lettersOnly";
  },
  email: (v) => {
    if (!v?.trim()) return "auth.validation.emailRequired";
    if (!isLikelyEmail(v)) return "auth.validation.emailInvalid";
  },
  phone: (v) => {
    if (!v?.trim()) return "auth.validation.phoneRequired";
    if (!isValidPhone(v)) return "auth.validation.phoneInvalid";
  },
  password: (v) => {
    if (!v) return "auth.validation.passwordRequired";
    if (!isValidPassword(v)) return PASSWORD_REQUIREMENTS_KEY;
  },
  confirmPassword: (v, form) => {
    if (!v) return "auth.validation.confirmPasswordRequired";
    if (form && v !== form.password) return "auth.validation.passwordsMismatch";
  },
  date_of_birth: (v) => {
    if (!v) return "auth.validation.dobRequired";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "auth.validation.invalidDate";
    if (d > new Date()) return "auth.validation.dobFuture";
    const age = calculateAge(v);
    if (age === null) return "auth.validation.invalidDate";
    if (age > 100) return "auth.validation.dobInvalid";
  },
  nationality: (v) => {
    if (!v?.trim()) return "auth.validation.nationalityRequired";
  },
  bio: (v) => {
    if (v && v.length > 1000) return "auth.validation.max1000Chars";
  },
  experience_years: (v) => {
    if (!v) return "auth.validation.experienceRequired";
  },
};

// Social media URL validators (separate because nested) — currently unused
// anywhere in the app (no caller found), left with a plain literal since
// there's no reachable path that displays it.
export const validateSocialUrl = (url: string): string | undefined => {
  if (!url) return undefined;
  if (!URL_RE.test(url) && !url.startsWith("@"))
    return "Enter a valid URL or @username";
};

// ── Password strength ─────────────────────────────────────────────────────────

export type PasswordStrength = "weak" | "fair" | "strong" | "very-strong";

export const getPasswordStrength = (
  password: string,
): { level: PasswordStrength; score: number } => {
  if (!password) return { level: "weak", score: 0 };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const level: PasswordStrength =
    score <= 2
      ? "weak"
      : score <= 3
        ? "fair"
        : score <= 4
          ? "strong"
          : "very-strong";
  return { level, score };
};

// ── Validation ───────────────────────────────────────────────────────────────

/** Validate a single field and return its error string (or undefined). */
export const validateField = (
  field: keyof TalentFormData,
  value: any,
  form?: TalentFormData,
): string | undefined => fieldValidators[field]?.(value, form);

export const validateBasicInfo = (f: TalentFormData): TalentFormErrors => {
  const errors: TalentFormErrors = {};
  const fields: (keyof TalentFormData)[] = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "password",
    "confirmPassword",
  ];
  for (const field of fields) {
    const err = fieldValidators[field]?.(f[field], f);
    if (err) (errors as any)[field] = err;
  }
  return errors;
};

export const validateTalentDetails = (f: TalentFormData): TalentFormErrors => {
  const errors: TalentFormErrors = {};

  // DOB — required, min 16
  const dobErr = fieldValidators.date_of_birth?.(f.date_of_birth);
  if (dobErr) errors.date_of_birth = dobErr;

  // Nationality — required
  const natErr = fieldValidators.nationality?.(f.nationality);
  if (natErr) errors.nationality = natErr;

  // Gender — must pick Male or Female from categories
  const hasGender =
    f.categories.includes("Male") || f.categories.includes("Female");
  if (!hasGender) errors.categories = "auth.validation.genderRequired";

  // Specialties
  if (!f.specialties.length)
    errors.specialties = "auth.validation.specialtyRequired";

  // Languages
  if (!f.languages.length) errors.languages = "auth.validation.languageRequired";
  if (f.languages.includes(OTHER_LANGUAGE_VALUE) && !f.custom_language?.trim())
    errors.languages = "auth.validation.customLanguageRequired";

  // Experience
  const expErr = fieldValidators.experience_years?.(f.experience_years);
  if (expErr) errors.experience_years = expErr;

  // Height / weight — numeric if provided. Both share the single
  // `measurements` error key (TalentFormErrors has no per-sub-field keys),
  // so append rather than overwrite when both are invalid.
  const measurementErrors: string[] = [];
  const h = f.measurements?.height;
  if (h && (isNaN(Number(h)) || Number(h) <= 0))
    measurementErrors.push("auth.validation.heightPositive");

  const w = f.measurements?.weight;
  if (w && (isNaN(Number(w)) || Number(w) <= 0))
    measurementErrors.push("auth.validation.weightPositive");

  if (measurementErrors.length) errors.measurements = measurementErrors.join(" / ");

  // Bio length
  const bioErr = fieldValidators.bio?.(f.bio);
  if (bioErr) errors.bio = bioErr;

  return errors;
};

// ── Age helpers ──────────────────────────────────────────────────────────────

// Returns a translation KEY — TalentDetailsStep.tsx (the sole consumer) has
// t() and translates it at render time.
export const getAgeCategory = (age: number): string => {
  if (age <= 2) return "auth.validation.ageCategoryInfants";
  if (age <= 5) return "auth.validation.ageCategoryToddler";
  if (age <= 11) return "auth.validation.ageCategoryKids";
  if (age <= 16) return "auth.validation.ageCategoryTeens";
  if (age <= 25) return "auth.validation.ageCategoryAdults";
  if (age <= 35) return "auth.validation.ageCategoryPrime";
  if (age <= 50) return "auth.validation.ageCategoryMature";
  return "auth.validation.ageCategoryClassic";
};

// ── Payload builder ──────────────────────────────────────────────────────────

export const buildRegistrationPayload = (
  f: TalentFormData,
  interests: string[],
) => {
  // Normalize "content-creation" (UI value) → "content_creation" (backend value)
  const normalized = interests.map((i) =>
    i === "content-creation" ? "content_creation" : i,
  );

  const talentType = normalized.includes("both")
    ? "both"
    : normalized.includes("modeling") && normalized.includes("content_creation")
      ? "both"
      : normalized.includes("modeling")
        ? "modeling"
        : normalized.includes("content_creation")
          ? "content_creation"
          : "modeling"; // safe fallback

  const fd = new FormData();
  fd.append("first_name", f.first_name);
  fd.append("last_name", f.last_name);
  fd.append("email", f.email);
  fd.append("password", f.password);
  fd.append("password_confirmation", f.confirmPassword);
  fd.append("role", "talent");

  // Send phone as-is so backend PhoneNormalizer can handle E.164 formatting
  fd.append("phone", f.phone);
  fd.append("phone_country", f.phone_country);

  fd.append("talent_type", talentType);
  fd.append("date_of_birth", f.date_of_birth);
  if (f.city) fd.append("city", f.city);
  if (f.nationality) fd.append("nationality", f.nationality);
  if (f.bio) fd.append("bio", f.bio);
  if (f.measurements?.height)
    fd.append("height", String(Number(f.measurements.height)));
  if (f.measurements?.weight)
    fd.append("weight", String(Number(f.measurements.weight)));
  if (f.measurements?.tshirtSize) fd.append("tshirt_size", f.measurements.tshirtSize);
  if (f.measurements?.pant) fd.append("pant_size", f.measurements.pant);
  if (f.measurements?.shoeSize) fd.append("shoe_size", f.measurements.shoeSize);
  if (f.rates) fd.append("rates_per", f.rates);

  if (f.skin_tone) fd.append("skin_tone", f.skin_tone);
  if (f.hair_type) fd.append("hair_type", f.hair_type);
  if (f.eye_color) fd.append("eye_color", f.eye_color);
  if (f.hair_color) fd.append("hair_color", f.hair_color);
  if (f.body_type) fd.append("body_type", f.body_type);

  const gender = f.categories.includes("Male")
    ? "male"
    : f.categories.includes("Female")
      ? "female"
      : null;
  if (gender) fd.append("gender", gender);

  // FIX: backend validates/persists this list under the "specializations" key
  // (see TalentController::TalentRegister) — it was previously sent as
  // "specialties_ids[]", a key the backend never read, so the required
  // Specialties selection was silently dropped at registration.
  f.specialties.forEach((id) => fd.append("specializations[]", String(id)));
  (f.specialty_options ?? []).forEach((id) => fd.append("specialty_options[]", String(id)));

  // The talents table has no separate `dialects` column — dialect codes ride
  // along as extra entries in the same `languages` json array.
  mergeLanguagesAndDialects(f.languages, f.dialects ?? [])
    .filter(Boolean)
    .forEach((l) => fd.append("languages[]", l));
  if (f.languages.includes(OTHER_LANGUAGE_VALUE) && f.custom_language)
    fd.append("custom_language", f.custom_language);

  if (f.look_alike) fd.append("look_alike", f.look_alike);

  if (f.experience_years) fd.append("experience_years", f.experience_years);

  if (f.socialMedia?.instagram) fd.append("social_media[instagram]", f.socialMedia.instagram);
  if (f.socialMedia?.facebook) fd.append("social_media[facebook]", f.socialMedia.facebook);
  if (f.socialMedia?.tiktok) fd.append("social_media[tiktok]", f.socialMedia.tiktok);

  return fd;
};
